import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  BoltIcon,
  UserIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  ArrowUpOnSquareIcon,
  BellIcon,
  BellAlertIcon,
  ArrowPathIcon,
  CommandLineIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import DeviceIcon from '../components/DeviceIcon';

const eventIcons = {
  // Connection events
  device_connected: <DeviceIcon className="w-5 h-5" />,
  device_disconnected: <DeviceIcon className="w-5 h-5" />,
  subscriber_connected: <UserIcon className="w-5 h-5" />,
  subscriber_disconnected: <UserIcon className="w-5 h-5" />,
  // Data events
  subscription_added: <CloudArrowUpIcon className="w-5 h-5" />,
  data_forwarded: <ArrowUpOnSquareIcon className="w-5 h-5" />,
  // Alarm events
  alarm_new: <BellAlertIcon className="w-5 h-5" />,
  alarm_update: <BellIcon className="w-5 h-5" />,
  alarm_cleared: <CheckCircleIcon className="w-5 h-5" />,
  // History sync events
  history_sync_started: <ArrowPathIcon className="w-5 h-5 animate-spin" />,
  history_sync_completed: <ArrowPathIcon className="w-5 h-5" />,
  history_sync_triggered: <ArrowPathIcon className="w-5 h-5" />,
  // User events
  user_login: <UserIcon className="w-5 h-5" />,
  // Terminal events
  terminal_session_created: <CommandLineIcon className="w-5 h-5" />,
  terminal_session_destroyed: <CommandLineIcon className="w-5 h-5" />,
  // System events
  system_status: <CpuChipIcon className="w-5 h-5" />,
  connected: <CheckCircleIcon className="w-5 h-5" />,
  heartbeat: <BoltIcon className="w-5 h-5" />,
};

const eventColors = {
  // Connection events
  device_connected: 'bg-green-100 text-green-700',
  device_disconnected: 'bg-red-100 text-red-700',
  subscriber_connected: 'bg-blue-100 text-blue-700',
  subscriber_disconnected: 'bg-yellow-100 text-yellow-700',
  // Data events
  subscription_added: 'bg-purple-100 text-purple-700',
  data_forwarded: 'bg-teal-100 text-teal-700',
  // Alarm events
  alarm_new: 'bg-red-100 text-red-700',
  alarm_update: 'bg-orange-100 text-orange-700',
  alarm_cleared: 'bg-green-100 text-green-700',
  // History sync events
  history_sync_started: 'bg-blue-100 text-blue-700',
  history_sync_completed: 'bg-green-100 text-green-700',
  history_sync_triggered: 'bg-purple-100 text-purple-700',
  // User events
  user_login: 'bg-green-100 text-green-700',
  // Terminal events
  terminal_session_created: 'bg-purple-100 text-purple-700',
  terminal_session_destroyed: 'bg-gray-100 text-gray-700',
  // System events
  system_status: 'bg-indigo-100 text-indigo-700',
  connected: 'bg-green-100 text-green-700',
  heartbeat: 'bg-blue-100 text-blue-700',
};

const EventCard = ({ event, index }) => {
  const navigate = useNavigate();
  
  const getEventTitle = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const handleDeviceClick = (serial) => {
    navigate(`/dashboard/devices/${serial}`);
  };

  const handleUserClick = (userId) => {
    navigate(`/dashboard/users/${userId}`);
  };

  const getEventMessage = (event) => {
    switch (event.type) {
      case 'device_connected':
        return (
          <span>
            Device{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
            {' '}connected from {event.ip}
          </span>
        );
      case 'device_disconnected':
        return (
          <span>
            Device{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
            {' '}disconnected
          </span>
        );
      case 'subscriber_connected':
        return (
          <span>
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => event.userId && handleUserClick(event.userId)}
            >
              {event.userEmail || event.userId || 'Unknown user'}
            </span>
            {' '}connected ({event.devicesCount || 0} devices)
          </span>
        );
      case 'subscriber_disconnected':
        return (
          <span>
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => event.userId && handleUserClick(event.userId)}
            >
              {event.userEmail || event.userId || 'Unknown user'}
            </span>
            {' '}disconnected
          </span>
        );
      case 'subscription_added':
        return (
          <span>
            Subscribed to {event.topic} on{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      case 'data_forwarded':
        return `${event.topic} → ${event.subscriberCount} subscribers`;
      case 'system_status':
        const devices = event.stats?.devicesConnected || 0;
        const subscribers = event.stats?.subscribersConnected || 0;
        return (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${devices > 0 ? 'bg-green-500' : 'bg-gray-400'} animate-pulse`} />
              <DeviceIcon className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-gray-900">{devices}</span>
              <span className="text-gray-600">{devices === 1 ? 'device' : 'devices'}</span>
            </div>
            <div className="text-gray-400">•</div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${subscribers > 0 ? 'bg-blue-500' : 'bg-gray-400'} animate-pulse`} />
              <UserIcon className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-gray-900">{subscribers}</span>
              <span className="text-gray-600">{subscribers === 1 ? 'user' : 'users'}</span>
            </div>
            <div className="ml-auto">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">System Health</span>
            </div>
          </div>
        );
      // Alarm events
      case 'alarm_new':
        return (
          <span>
            New {event.severity} alarm: {event.message} on{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      case 'alarm_update':
        return (
          <span>
            Alarm updated: {event.message} on{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      case 'alarm_cleared':
        return (
          <span>
            Alarm cleared: {event.message} on{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      // History sync events
      case 'history_sync_started':
        return (
          <span>
            Sync started for{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.deviceSerial)}
            >
              {event.deviceSerial}
            </span>
            {' '}({event.totalRecords} records)
          </span>
        );
      case 'history_sync_completed':
        return (
          <span>
            Sync completed for{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.deviceSerial)}
            >
              {event.deviceSerial}
            </span>
            {' '}({event.recordsSynced} records in {(event.duration/1000).toFixed(1)}s)
          </span>
        );
      case 'history_sync_triggered':
        return (
          <span>
            Manual sync triggered for{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.deviceSerial)}
            >
              {event.deviceSerial}
            </span>
          </span>
        );
      // User events
      case 'user_login':
        return (
          <span>
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => event.userId && handleUserClick(event.userId)}
            >
              {event.userEmail}
            </span>
            {' '}logged in from {event.ip}
          </span>
        );
      // Terminal events
      case 'terminal_session_created':
        return (
          <span>
            Terminal session created for{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      case 'terminal_session_destroyed':
        return (
          <span>
            Terminal session ended for{' '}
            <span 
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
              onClick={() => handleDeviceClick(event.serial)}
            >
              {event.serial}
            </span>
          </span>
        );
      // System events
      case 'connected':
        return 'Successfully connected to event stream';
      case 'heartbeat':
        return 'Connection heartbeat';
      default:
        // For unknown events, try to create a meaningful message
        if (event.message) return event.message;
        if (event.data) return typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        return `${event.type} event`;
    }
  };

  const isSuccess = event.type?.includes('connected') && !event.type?.includes('disconnected') || event.type?.includes('cleared') || event.type?.includes('completed');
  const isError = event.type?.includes('disconnected') || event.type?.includes('error') || event.type === 'alarm_new';
  const isWarning = event.type === 'alarm_update' || event.severity === 'warning';
  const isHeartbeat = event.type === 'heartbeat';

  // Special compact view for heartbeat
  if (isHeartbeat) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.2 }}
        className="group"
      >
        <div className="bg-gray-50/50 rounded-lg px-3 py-1.5 flex items-center gap-2 border border-gray-100 hover:border-gray-200 transition-all">
          <BoltIcon className="w-3 h-3 text-gray-400 animate-pulse" />
          <span className="text-xs text-gray-500">Connection active</span>
          <span className="text-xs text-gray-400 ml-auto">
            {format(new Date(event.timestamp), 'HH:mm:ss')}
          </span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30,
        delay: index * 0.02 
      }}
      className="group"
    >
      <div className={`
        bg-white rounded-xl shadow-sm border transition-all duration-300
        hover:shadow-lg hover:-translate-y-0.5
        ${isError ? 'border-red-200 bg-red-50/30' : 
          isWarning ? 'border-orange-200 bg-orange-50/30' :
          isSuccess ? 'border-green-200 bg-green-50/30' :
          event.type === 'system_status' ? 'border-indigo-200 bg-indigo-50/20' :
          'border-gray-200'}
        ${event.type === 'system_status' ? 'shadow-md' : ''}
      `}>
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={`
              p-2.5 rounded-xl transition-colors
              ${eventColors[event.type] || eventColors.system_status}
              ${isError ? 'animate-pulse' : ''}
            `}>
              {eventIcons[event.type] || <BoltIcon className="w-5 h-5" />}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <h4 className="text-sm font-semibold text-gray-900">
                  {getEventTitle(event.type)}
                </h4>
                {isSuccess && <CheckCircleIcon className="w-4 h-4 text-green-600" />}
                {isError && <XCircleIcon className="w-4 h-4 text-red-600" />}
                {isWarning && <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />}
                {event.severity === 'critical' && <span className="px-2 py-0.5 text-xs font-semibold bg-red-600 text-white rounded-full">CRITICAL</span>}
                {event.severity === 'high' && <span className="px-2 py-0.5 text-xs font-semibold bg-orange-600 text-white rounded-full">HIGH</span>}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {getEventMessage(event)}
              </p>
              {/* Additional details for alarms */}
              {event.type?.includes('alarm') && event.sensorName && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                  <span className="font-medium">Sensor:</span> {event.sensorName}
                  {event.value && <><span className="mx-1">•</span><span className="font-medium">Value:</span> {event.value}</>}
                  {event.threshold && <><span className="mx-1">•</span><span className="font-medium">Threshold:</span> {event.threshold}</>}
                </div>
              )}
              {/* Additional details for sync events */}
              {event.type?.includes('sync') && event.recordsSynced && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <span className="font-medium">Records:</span> {event.recordsSynced}
                  {event.batchSize && <><span className="mx-1">•</span><span className="font-medium">Batch:</span> {event.batchSize}</>}
                </div>
              )}
              {/* Show system status details */}
              {event.type === 'system_status' && event.stats && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {/* Devices Card */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <DeviceIcon className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-green-800">Connected Devices</span>
                    </div>
                    
                    {event.stats.devices && event.stats.devices.length > 0 ? (
                      <div className="space-y-1.5">
                        {event.stats.devices.slice(0, 3).map((device, idx) => (
                          <div key={idx} className="bg-white/70 rounded px-2 py-1 hover:bg-white/90 cursor-pointer transition-colors"
                               onClick={() => handleDeviceClick(device.serial)}>
                            <div className="text-xs font-mono text-green-700 hover:text-green-800 underline">{device.serial}</div>
                            <div className="text-[10px] text-green-600">
                              {device.ip} • {formatDistanceToNow(new Date(device.connectedAt), { addSuffix: true })}
                            </div>
                          </div>
                        ))}
                        {event.stats.devices.length > 3 && (
                          <div className="text-[10px] text-green-600 italic text-center">
                            +{event.stats.devices.length - 3} more devices
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-green-600 italic">No devices connected</div>
                    )}
                  </div>
                  
                  {/* Subscribers Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <UserIcon className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-blue-800">Active Subscribers</span>
                    </div>
                    
                    {event.stats.subscribers && event.stats.subscribers.length > 0 ? (
                      <div className="space-y-1.5">
                        {event.stats.subscribers.slice(0, 3).map((sub, idx) => {
                          // Get user info - now available at root level
                          const userEmail = sub.email || sub.userId || 'Unknown User';
                          const userName = sub.name || '';
                          
                          // Get role from first socket if not at root
                          const firstSocket = sub.sockets?.[0] || {};
                          const userRole = firstSocket.userInfo?.role || '';
                          
                          return (
                            <div key={idx} className="bg-white/70 rounded px-2 py-1 hover:bg-white/90 cursor-pointer transition-colors"
                                 onClick={() => handleUserClick(sub.userId)}>
                              <div className="text-xs text-blue-700 font-medium truncate hover:text-blue-800 underline" title={userEmail}>
                                {userEmail}
                              </div>
                              <div className="text-[10px] text-blue-600">
                                {userName && <span className="font-medium">{userName} • </span>}
                                {sub.socketCount} {sub.socketCount === 1 ? 'connection' : 'connections'}
                                {sub.subscriptions?.length > 0 && ` • ${sub.subscriptions.length} subs`}
                                {userRole && <span className="text-blue-500"> • {userRole}</span>}
                              </div>
                            </div>
                          );
                        })}
                        {event.stats.subscribers.length > 3 && (
                          <div className="text-[10px] text-blue-600 italic text-center">
                            +{event.stats.subscribers.length - 3} more subscribers
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-blue-600 italic">No active subscribers</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show raw data for debugging if needed */}
              {event.type === 'connected' && event.id && (
                <div className="mt-2 text-xs text-gray-400">
                  Event ID: {event.id}
                </div>
              )}
            </div>

            {/* Timestamp */}
            <div className="text-xs text-gray-500 whitespace-nowrap text-right ml-4">
              <div className="font-semibold text-gray-700">{format(new Date(event.timestamp), 'HH:mm:ss')}</div>
              <div className="text-gray-400 text-[10px]">
                {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ModernLiveEvents = ({ events = [] }) => {
  const [localEvents, setLocalEvents] = useState(events);
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isPaused, setIsPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [isCleared, setIsCleared] = useState(false);
  const [showHeartbeats, setShowHeartbeats] = useState(false);
  const eventsEndRef = useRef(null);
  const lastEventCountRef = useRef(events.length);

  // Handle incoming events
  useEffect(() => {
    // If cleared, don't accept new events
    if (isCleared) return;
    
    // If paused, don't update local events
    if (!isPaused) {
      setLocalEvents(events);
    } else {
      // When paused, only add events that arrived after pause
      const newEventCount = events.length;
      if (newEventCount > lastEventCountRef.current) {
        // Store the count but don't update the display
        lastEventCountRef.current = newEventCount;
      }
    }
  }, [events, isPaused, isCleared]);
  
  // Filter events
  useEffect(() => {
    let filtered = localEvents;
    
    // Hide heartbeats unless explicitly shown
    if (!showHeartbeats) {
      filtered = filtered.filter(event => event.type !== 'heartbeat');
    }
    
    if (searchTerm) {
      filtered = filtered.filter(event => 
        JSON.stringify(event).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(event => event.type === typeFilter);
    }
    
    setFilteredEvents(filtered);
  }, [localEvents, searchTerm, typeFilter, showHeartbeats]);

  useEffect(() => {
    if (autoScroll && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredEvents, autoScroll]);

  const eventTypes = [...new Set(events.map(e => e.type))];
  const pendingEventCount = isPaused ? events.length - lastEventCountRef.current : 0;
  
  // Find the most recent heartbeat
  const lastHeartbeat = localEvents.filter(e => e.type === 'heartbeat').slice(-1)[0];
  const connectionStatus = lastHeartbeat ? 
    new Date() - new Date(lastHeartbeat.timestamp) < 35000 ? 'active' : 'stale' 
    : 'unknown';

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Live Events</h1>
            <p className="text-gray-600 mt-1">Real-time system activity</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Connection Status */}
            <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${
              connectionStatus === 'active' ? 'bg-green-50 border border-green-200' :
              connectionStatus === 'stale' ? 'bg-yellow-50 border border-yellow-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'active' ? 'bg-green-500 animate-pulse' :
                connectionStatus === 'stale' ? 'bg-yellow-500' :
                'bg-gray-400'
              }`} />
              <span className={`text-xs font-medium ${
                connectionStatus === 'active' ? 'text-green-700' :
                connectionStatus === 'stale' ? 'text-yellow-700' :
                'text-gray-600'
              }`}>
                {connectionStatus === 'active' ? 'Connected' :
                 connectionStatus === 'stale' ? 'Connection stale' :
                 'No heartbeat'}
              </span>
            </div>
            
            {isPaused && pendingEventCount > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="px-3 py-2 bg-yellow-100 rounded-lg shadow-sm border border-yellow-300"
              >
                <span className="text-sm font-semibold text-yellow-800">
                  {pendingEventCount} new {pendingEventCount === 1 ? 'event' : 'events'} while paused
                </span>
              </motion.div>
            )}
            <button
              onClick={() => setShowStats(!showStats)}
              className="p-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50"
            >
              <ChartBarIcon className="w-5 h-5 text-gray-700" />
            </button>
            <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200">
              <span className="text-2xl font-bold text-gray-900">{filteredEvents.length}</span>
              <span className="text-sm text-gray-500 ml-2">events</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Statistics */}
      {showStats && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-red-50 to-white rounded-xl shadow-sm border border-red-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-red-600">
                  {filteredEvents.filter(e => e.type?.includes('alarm_new')).length}
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">Active Alarms</div>
              </div>
              <BellAlertIcon className="w-8 h-8 text-red-400" />
            </div>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-sm border border-green-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-green-600">
                  {filteredEvents.filter(e => e.type === 'device_connected').length}
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">Devices Online</div>
              </div>
              <DeviceIcon className="w-8 h-8 text-green-400" />
            </div>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm border border-blue-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {filteredEvents.filter(e => e.type?.includes('sync')).length}
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">Sync Events</div>
              </div>
              <ArrowPathIcon className="w-8 h-8 text-blue-400" />
            </div>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm border border-purple-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {filteredEvents.filter(e => e.type === 'user_login').length}
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">User Logins</div>
              </div>
              <UserIcon className="w-8 h-8 text-purple-400" />
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
          >
            <option value="all">All Types</option>
            {eventTypes.map(type => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          {/* Controls */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowHeartbeats(!showHeartbeats)}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                showHeartbeats
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showHeartbeats ? 'Hide heartbeat events' : 'Show heartbeat events'}
            >
              <BoltIcon className="w-4 h-4" />
              Heartbeats
            </button>

            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                autoScroll
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Auto Scroll
            </button>

            <button
              onClick={() => {
                setIsPaused(!isPaused);
                // If resuming from pause, update with any events that arrived while paused
                if (isPaused && !isCleared) {
                  setLocalEvents(events);
                  lastEventCountRef.current = events.length;
                }
              }}
              className={`p-2.5 rounded-lg transition-all ${
                isPaused 
                  ? 'bg-yellow-100 hover:bg-yellow-200 border border-yellow-300' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              title={isPaused ? 'Resume event updates' : 'Pause event updates'}
            >
              {isPaused ? (
                <PlayIcon className="w-5 h-5 text-yellow-700" />
              ) : (
                <PauseIcon className="w-5 h-5 text-gray-700" />
              )}
            </button>

            <button
              onClick={() => {
                const dataStr = JSON.stringify(filteredEvents, null, 2);
                const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                const exportFileDefaultName = `events_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.json`;
                const linkElement = document.createElement('a');
                linkElement.setAttribute('href', dataUri);
                linkElement.setAttribute('download', exportFileDefaultName);
                linkElement.click();
              }}
              className="p-2.5 bg-gray-100 hover:bg-green-100 rounded-lg transition-all group"
            >
              <ArrowDownTrayIcon className="w-5 h-5 text-gray-700 group-hover:text-green-600" />
            </button>

            <button
              onClick={() => {
                setLocalEvents([]);
                setFilteredEvents([]);
                setIsCleared(true);
                lastEventCountRef.current = events.length;
                
                // Show a visual feedback
                setTimeout(() => {
                  setIsCleared(false);
                  // If not paused, start accepting new events after clear
                  if (!isPaused) {
                    lastEventCountRef.current = events.length;
                  }
                }, 100);
              }}
              className="p-2.5 bg-gray-100 hover:bg-red-100 rounded-lg transition-all group"
              title="Clear all events"
            >
              <TrashIcon className="w-5 h-5 text-gray-700 group-hover:text-red-600" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Events List */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="h-[600px] overflow-y-auto p-6">
          <AnimatePresence mode="popLayout">
            {filteredEvents.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center h-full"
              >
                <div className="p-6 bg-white rounded-full shadow-lg mb-6">
                  <BoltIcon className="w-16 h-16 text-gray-400" />
                </div>
                <p className="text-gray-700 text-xl font-semibold">No events to display</p>
                <p className="text-gray-500 text-sm mt-2">
                  {isPaused ? 'Events are paused' : 'Events will appear here as they occur'}
                </p>
              </motion.div>
            ) : (
              <div className="space-y-3">
                {filteredEvents.slice(-50).reverse().map((event, index) => (
                  <EventCard key={event.id || `${event.type}-${event.timestamp}-${index}`} event={event} index={index} />
                ))}
                <div ref={eventsEndRef} />
              </div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ModernLiveEvents;